import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Brain,
  Heart,
  Sparkles,
  Star,
  Zap,
  Target,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Activity,
  Shield
} from 'lucide-react';
import api from '../utils/api';

const Assessment = () => {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [assessment, setAssessment] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAssessment();
    setStartTime(Date.now());
  }, [assessmentId]);

  const fetchAssessment = async () => {
    try {
      const response = await api.get(`/assessments/${assessmentId}`);
      setAssessment(response.data.assessment);
      
      // Initialize responses array
      const initialResponses = response.data.assessment.questions.map((_, index) => ({
        index,
        answer: null,
        category: response.data.assessment.questions[index].category
      }));
      setResponses(initialResponses);
    } catch (error) {
      console.error('Failed to fetch assessment:', error);
      setError('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answer) => {
    const updatedResponses = [...responses];
    updatedResponses[currentQuestion].answer = answer;
    setResponses(updatedResponses);
  };

  const nextQuestion = () => {
    if (currentQuestion < assessment.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const canProceed = () => {
    const currentResponse = responses[currentQuestion];
    if (!currentResponse || currentResponse.answer === null) return false;
    
    const question = assessment.questions[currentQuestion];
    if (question.type === 'text' && (!currentResponse.answer || currentResponse.answer.trim() === '')) {
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;

    setSubmitting(true);
    try {
      const duration = Math.round((Date.now() - startTime) / 1000 / 60); // minutes
      
      const response = await api.post(`/assessments/${assessmentId}/submit`, {
        responses: responses.map(r => ({ answer: r.answer, category: r.category })),
        duration
      });

      // Check if there's a returnTo parameter in the URL
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get('returnTo');

      if (returnTo) {
        // Navigate back to the chat session
        navigate(`/${returnTo}`);
      } else {
              // Navigate to assessment response page
      navigate(`/app/assessment-response/${response.data.responseId}`, {
          state: {
            assessmentName: assessment.name,
            therapistName: location.state?.therapistName || 'Unknown',
            therapistSpecialty: location.state?.therapistSpecialty || 'Unknown'
          }
        });
      }
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      setError('Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = () => {
    if (!assessment || !assessment.questions[currentQuestion]) return null;

    const question = assessment.questions[currentQuestion];
    const currentResponse = responses[currentQuestion];

    switch (question.type) {
      case 'multiple_choice':
        return (
          <div className="space-y-4">
            {question.options.map((option, index) => (
              <label key={index} className="flex items-center space-x-4 p-4 border-2 border-gray-100 rounded-2xl cursor-pointer hover:border-blue-200 hover:bg-blue-50 transition-all duration-200 group">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  currentResponse?.answer === option
                    ? 'bg-gradient-to-br from-blue-500 to-purple-600 border-blue-500'
                    : 'border-gray-300 group-hover:border-blue-400'
                }`}>
                  {currentResponse?.answer === option && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <input
                  type="radio"
                  name={`question-${currentQuestion}`}
                  value={option}
                  checked={currentResponse?.answer === option}
                  onChange={(e) => handleAnswer(e.target.value)}
                  className="sr-only"
                />
                <span className="text-gray-700 font-medium leading-relaxed">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'scale':
        return (
          <div className="space-y-6">
            <div className="flex justify-between text-sm text-gray-500 font-medium">
              <span>{question.scaleRange.min}</span>
              <span>{question.scaleRange.max}</span>
            </div>
            <div className="grid grid-cols-10 gap-1 sm:gap-2 px-1 sm:px-2">
              {Array.from({ length: question.scaleRange.max - question.scaleRange.min + 1 }, (_, i) => {
                const value = i + question.scaleRange.min;
                return (
                  <button
                    key={value}
                    onClick={() => handleAnswer(value)}
                    className={`aspect-square w-full max-w-[32px] sm:max-w-[40px] md:max-w-[48px] lg:max-w-[56px] rounded-md sm:rounded-lg md:rounded-xl border-2 flex items-center justify-center transition-all duration-200 transform hover:scale-110 ${
                      currentResponse?.answer === value
                        ? 'bg-gradient-to-br from-blue-500 to-purple-600 border-blue-500 text-white shadow-lg'
                        : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <span className="font-bold text-xs sm:text-sm md:text-base">{value}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            <textarea
              value={currentResponse?.answer || ''}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder="Type your answer here..."
              className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200 hover:border-gray-300"
              rows={6}
            />
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Sparkles className="w-4 h-4" />
              <span>Share as much detail as you're comfortable with</span>
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-4">
            {question.options.map((option, index) => (
              <label key={index} className="flex items-center space-x-4 p-4 border-2 border-gray-100 rounded-2xl cursor-pointer hover:border-blue-200 hover:bg-blue-50 transition-all duration-200 group">
                <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                  currentResponse?.answer?.includes?.(option)
                    ? 'bg-gradient-to-br from-blue-500 to-purple-600 border-blue-500'
                    : 'border-gray-300 group-hover:border-blue-400'
                }`}>
                  {currentResponse?.answer?.includes?.(option) && (
                    <CheckCircle className="w-3 h-3 text-white" />
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={currentResponse?.answer?.includes?.(option) || false}
                  onChange={(e) => {
                    const currentAnswers = currentResponse?.answer || [];
                    const newAnswers = e.target.checked
                      ? [...currentAnswers, option]
                      : currentAnswers.filter(a => a !== option);
                    handleAnswer(newAnswers);
                  }}
                  className="sr-only"
                />
                <span className="text-gray-700 font-medium leading-relaxed">{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Assessment...</h3>
          <p className="text-gray-600">Preparing your personalized questions</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-pink-50">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-100 to-pink-100 rounded-3xl mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Assessment</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/app/assessments')}
            className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Back to Assessments
          </button>
        </div>
      </div>
    );
  }

  if (!assessment) return null;

  const progress = ((currentQuestion + 1) / assessment.questions.length) * 100;
  const isLastQuestion = currentQuestion === assessment.questions.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
        {/* Beautiful Header */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-xl border border-blue-100 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <button
              onClick={() => navigate('/app/assessments')}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-xl transition-all duration-200 transform hover:scale-105 w-fit"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to Assessments</span>
            </button>
            <div className="flex items-center space-x-2 text-sm text-gray-500 bg-white px-4 py-2 rounded-xl shadow-sm w-fit">
              <Clock className="w-4 h-4 text-blue-500" />
              <span className="font-medium">Question {currentQuestion + 1} of {assessment.questions.length}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500 shadow-lg"
              style={{ width: `${progress}%` }}
            ></div>
          </div>

          {/* Assessment Info */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl sm:rounded-3xl mb-4 shadow-lg">
              <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{assessment.name}</h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">{assessment.description}</p>
            {location.state?.therapistName && (
              <div className="flex items-center justify-center space-x-2 mt-3 text-sm text-gray-500">
                <Heart className="w-4 h-4 text-red-500" />
                <span>by {location.state.therapistName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8">
          <div className="flex items-start space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex-shrink-0">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
              {assessment.questions[currentQuestion].question}
            </h2>
          </div>

          {renderQuestion()}

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={previousQuestion}
              disabled={currentQuestion === 0}
              className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 border-2 border-gray-200 text-gray-600 rounded-xl hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="font-medium">Previous</span>
            </button>

            {isLastQuestion ? (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || submitting}
                className="flex items-center justify-center space-x-2 px-6 sm:px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span className="font-semibold">Submitting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Submit Assessment</span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={nextQuestion}
                disabled={!canProceed()}
                className="flex items-center justify-center space-x-2 px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span className="font-semibold">Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assessment; 